import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { FormSummaryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { formsApi } from '@/services/forms.api';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';
import { Alert, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';
import { FormCreateDialog } from './form-create-dialog';

/**
 * The Forms directory.
 *
 * Deliberately the same shape as Groups' and Events' directories: same search
 * behaviour, same archived toggle, same empty and error states.
 */
export function FormsListPage() {
  const { t } = useI18n();
  const { can } = useAuth();

  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  // Controlled exclusively by My Settings — no per-page override, so an
  // archived record is never one accidental checkbox away from view.
  const showArchived = getShowArchivedRecordsPreference();

  const deferredSearch = useDeferredValue(search);

  const query = useQuery({
    queryKey: ['forms', { search: deferredSearch, showArchived }],
    queryFn: ({ signal }) =>
      formsApi.list(
        { search: deferredSearch || undefined, includeArchived: showArchived, limit: 50 },
        signal,
      ),
    placeholderData: (previous) => previous,
  });

  const forms = query.data?.data ?? [];
  const isStale = query.isPlaceholderData;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('forms.form.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('forms.form.count', { count: forms.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>

        {can('forms.form.create') && (
          <button
            type="button"
            className="btn"
            data-variant="primary"
            onClick={() => setIsCreating(true)}
          >
            {t('forms.form.create')}
          </button>
        )}
      </header>

      <div className="filters">
        <Field
          label={t('common.actions.search')}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('common.actions.search')}
          name="search"
        />
      </div>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && forms.length === 0 && (
        <EmptyState
          title={search ? t('common.states.noResults') : t('forms.form.empty')}
          hint={search ? undefined : t('forms.form.create')}
        />
      )}

      {forms.length > 0 && (
        <div className="table-scroll" data-stale={isStale || undefined}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('forms.form.singular')}</th>
                <th scope="col">{t('forms.form.fieldCount')}</th>
                <th scope="col">{t('forms.submission.title')}</th>
                <th scope="col">{t('common.labels.status')}</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((form) => (
                <FormRow key={form.id} form={form} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreating && (
        <FormCreateDialog
          onClose={() => setIsCreating(false)}
          onCreated={() => {
            setIsCreating(false);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}

function FormRow({ form }: { form: FormSummaryResponse }) {
  const { t } = useI18n();

  return (
    <tr data-archived={form.archivedAt ? true : undefined}>
      <td>
        <Link to={`/forms/${form.id}`} className="person-name">
          {form.title}
        </Link>
      </td>
      <td dir="ltr">{form.fieldCount}</td>
      <td dir="ltr">{form.submissionCount}</td>
      <td>
        <StatusBadge status={form.status} label={t(`forms.status.${form.status}`)} />
      </td>
    </tr>
  );
}
